import axios from 'axios'

import {
  prepareData,
  prepareFileFormData,
  prepareFormData,
  processAxiosResponse,
} from './data'
import { handle as handleError } from './errors'

// Scoped instance keeps our interceptor and defaults off the global axios singleton.
export const apiClient = axios.create({
  timeout: 15000,
  withCredentials: true,
})

const RETRY_DELAYS = [250, 1000]

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const isTransientFailure = error => {
  if (!error.response) return true
  const status = error.response.status
  return status >= 500 && status < 600
}

// Writes only retry with an Idempotency-Key — without server-side dedupe, retrying risks double-charging/redeeming.
const isRetryableMethod = config => {
  const method = (config.method || 'get').toLowerCase()
  if (method === 'get') return true
  return Boolean(config.headers?.['Idempotency-Key'])
}

const shouldRetry = (config, error) => {
  const attempt = config.axiosRetryCount ?? 0
  if (attempt >= RETRY_DELAYS.length) return false
  if (!isRetryableMethod(config)) return false
  return isTransientFailure(error)
}

apiClient.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config
    if (!config || !shouldRetry(config, error)) return Promise.reject(error)

    // Attempt count rides on the config object — axios reuses it across interceptor recursion.
    const nextAttempt = (config.axiosRetryCount ?? 0) + 1
    config.axiosRetryCount = nextAttempt
    await sleep(RETRY_DELAYS[nextAttempt - 1])
    return apiClient(config)
  }
)

const errorOptions = ({ data, name, url }) => {
  const fingerprint = `${name}: ${url.replace(/\d+/g, 'XX')}`

  return {
    fingerprint: fingerprint,
    name: fingerprint,
    params: data
  }
}

const axiosResponse = ({ method, url, data }) => processAxiosResponse(
  apiClient({ data: prepareData(data), method, url })
).catch(error => handleError(error, errorOptions({
  data,
  name: `HttpAxiosResponse-${method}`,
  url
})))

const axiosGet = ({ url, data }) => processAxiosResponse(
  apiClient.get(url, { params: prepareData(data) })
).catch(error => handleError(error, errorOptions({
  data,
  name: 'HttpAxiosGet',
  url
})))

const axiosDelete = ({ url, data }) => processAxiosResponse(
  apiClient.delete(url, { params: prepareData(data) })
).catch(error => handleError(error, errorOptions({
  data,
  name: 'HttpAxiosDelete',
  url
})))

const axiosPost = ({ url, data, requestConfig = {} }) => processAxiosResponse(
  apiClient.post(url, prepareData(data), requestConfig)
).catch(error => handleError(error, errorOptions({
  data,
  name: 'HttpAxiosPost',
  url
})))

const axiosForm = ({ method, requestConfig = {}, url, options }) => {
  const formData = prepareFormData(options)
  const axiosFormOptions = {
    ...requestConfig,
    headers: {
      ...requestConfig.headers,
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }

  return processAxiosResponse(
    apiClient[method](url, formData, axiosFormOptions)
  ).catch(error => handleError(error, errorOptions({
    data: options,
    name: 'HttpAxiosForm',
    url
  })))
}

const axiosPostFile = ({ url, file, options }) => {
  const formData = prepareFileFormData({ file, options })
  const axiosFormOptions = {
    headers: { 'Content-Type': 'multipart/form-data' }
  }

  return processAxiosResponse(
    apiClient.post(url, formData, axiosFormOptions)
  ).catch(error => handleError(error, errorOptions({
    data: { file, ...options },
    name: 'HttpAxiosPostFile',
    url
  })))
}

export default {
  delete: (url, data) => axiosDelete({ data, url }),
  get: (url, data) => axiosGet({ data, url }),
  patch: (url, data) => axiosResponse({ data, method: 'patch', url }),
  post: (url, data, requestConfig) => axiosPost({ data, requestConfig, url }),
  postFile: (url, file, options) => axiosPostFile({ file, options, url }),
  postForm: (url, options, requestConfig) => axiosForm({
    method: 'post', options, requestConfig, url
  }),
  put: (url, data) => axiosResponse({ data, method: 'put', url }),
  putForm: (url, options, requestConfig) => axiosForm({
    method: 'put', options, requestConfig, url
  }),
}
